(function () {
  "use strict";

  var PRODUCT_SELECTOR = ".shopify-section--main-product .product";
  var DESKTOP_QUERY = "(min-width: 1000px)";
  var REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
  var ACTIVE_CLASS = "qk-product--gallery-sync";
  var DISTANCE_PROPERTY = "--qk-gallery-sync-distance";
  var START_PROPERTY = "--qk-gallery-sync-start";
  var END_PROPERTY = "--qk-gallery-sync-end";
  var BOTTOM_GAP = 20;
  var supportsScrollTimeline =
    typeof CSS !== "undefined" &&
    CSS.supports("animation-timeline: scroll(root block)") &&
    CSS.supports("animation-range: 0px 1px");
  var instances = new WeakMap();

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function getStickyTopOffset() {
    var probe = document.createElement("div");

    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = [
      "position:fixed",
      "top:calc(var(--sticky-area-height, 0px) + 20px)",
      "width:0",
      "height:0",
      "visibility:hidden",
      "pointer-events:none",
    ].join(";");

    document.documentElement.appendChild(probe);
    var offset = probe.getBoundingClientRect().top;
    probe.remove();

    return Math.max(0, offset);
  }

  function GallerySync(product) {
    this.product = product;
    this.gallery = product.querySelector(":scope > .product-gallery");
    this.info = product.querySelector(":scope > .product-info");
    this.desktopMedia = window.matchMedia(DESKTOP_QUERY);
    this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_QUERY);
    this.resizeObserver = null;
    this.frame = null;
    this.metrics = null;
    this.lastOffset = null;
    this.initialTransform = this.gallery ? this.gallery.style.transform : "";
    this.destroyed = false;
    this.onScroll = this.scheduleUpdate.bind(this);
    this.onEnvironmentChange = this.scheduleMeasure.bind(this);

    if (!this.gallery || !this.info) {
      return;
    }

    if (!supportsScrollTimeline) {
      window.addEventListener("scroll", this.onScroll, { passive: true });
    }

    window.addEventListener("resize", this.onEnvironmentChange, { passive: true });
    this.desktopMedia.addEventListener("change", this.onEnvironmentChange);
    this.reducedMotionMedia.addEventListener("change", this.onEnvironmentChange);

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(this.onEnvironmentChange);
      this.resizeObserver.observe(this.gallery);
      this.resizeObserver.observe(this.info);
    }

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.onEnvironmentChange);
    }

    this.scheduleMeasure();
  }

  GallerySync.prototype.scheduleMeasure = function () {
    var instance = this;

    if (this.destroyed) {
      return;
    }

    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
    }

    this.frame = requestAnimationFrame(function () {
      instance.frame = null;
      instance.measure();
      instance.update();
    });
  };

  GallerySync.prototype.scheduleUpdate = function () {
    var instance = this;

    if (this.destroyed || this.frame !== null) {
      return;
    }

    this.frame = requestAnimationFrame(function () {
      instance.frame = null;
      instance.update();
    });
  };

  GallerySync.prototype.measure = function () {
    this.metrics = null;

    if (!this.desktopMedia.matches || this.reducedMotionMedia.matches) {
      this.reset();
      return;
    }

    var galleryHeight = this.gallery.offsetHeight;
    var infoHeight = this.info.offsetHeight;

    if (infoHeight <= galleryHeight + 1) {
      this.reset();
      return;
    }

    var stickyTop = getStickyTopOffset();
    var availableHeight = Math.max(1, window.innerHeight - stickyTop - BOTTOM_GAP);
    var infoScrollRange = Math.max(0, infoHeight - availableHeight);
    var galleryScrollRange = Math.max(0, galleryHeight - availableHeight);

    if (infoScrollRange <= 0 || infoScrollRange <= galleryScrollRange) {
      this.reset();
      return;
    }

    this.metrics = {
      start: this.product.getBoundingClientRect().top + window.scrollY - stickyTop,
      scrollRange: infoScrollRange,
      translateRange: infoScrollRange - galleryScrollRange,
    };

    if (supportsScrollTimeline) {
      var timelineStart = Math.max(0, this.metrics.start);

      this.gallery.style.setProperty(DISTANCE_PROPERTY, this.metrics.translateRange.toFixed(2) + "px");
      this.gallery.style.setProperty(START_PROPERTY, timelineStart.toFixed(2) + "px");
      this.gallery.style.setProperty(
        END_PROPERTY,
        (timelineStart + this.metrics.scrollRange).toFixed(2) + "px"
      );
    }

    this.product.classList.add(ACTIVE_CLASS);
  };

  GallerySync.prototype.update = function () {
    if (!this.metrics || supportsScrollTimeline) {
      return;
    }

    var progress = clamp(
      (window.scrollY - this.metrics.start) / this.metrics.scrollRange,
      0,
      1
    );
    var offset = Math.round(this.metrics.translateRange * progress * 100) / 100;

    if (offset === this.lastOffset) {
      return;
    }

    this.lastOffset = offset;
    this.gallery.style.transform = "translate3d(0," + offset + "px,0)";
  };

  GallerySync.prototype.reset = function () {
    this.product.classList.remove(ACTIVE_CLASS);
    this.lastOffset = null;
    this.gallery.style.removeProperty(DISTANCE_PROPERTY);
    this.gallery.style.removeProperty(START_PROPERTY);
    this.gallery.style.removeProperty(END_PROPERTY);

    if (this.initialTransform) {
      this.gallery.style.transform = this.initialTransform;
    } else {
      this.gallery.style.removeProperty("transform");
    }
  };

  GallerySync.prototype.destroy = function () {
    if (!this.gallery || !this.info) {
      return;
    }

    this.destroyed = true;

    if (!supportsScrollTimeline) {
      window.removeEventListener("scroll", this.onScroll);
    }
    window.removeEventListener("resize", this.onEnvironmentChange);
    this.desktopMedia.removeEventListener("change", this.onEnvironmentChange);
    this.reducedMotionMedia.removeEventListener("change", this.onEnvironmentChange);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
    }

    this.reset();
  };

  function findProducts(root) {
    var products = [];

    if (root instanceof Element && root.matches(PRODUCT_SELECTOR)) {
      products.push(root);
    }

    if (root && root.querySelectorAll) {
      products = products.concat(Array.prototype.slice.call(root.querySelectorAll(PRODUCT_SELECTOR)));
    }

    return products;
  }

  function init(root) {
    findProducts(root || document).forEach(function (product) {
      if (instances.has(product)) {
        return;
      }

      var instance = new GallerySync(product);

      if (instance.gallery && instance.info) {
        instances.set(product, instance);
      }
    });
  }

  function destroy(root) {
    findProducts(root).forEach(function (product) {
      var instance = instances.get(product);

      if (!instance) {
        return;
      }

      instance.destroy();
      instances.delete(product);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener("shopify:section:load", function (event) {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", function (event) {
    destroy(event.target);
  });
})();
